/* eslint-disable object-shorthand */
import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Card } from "../Card";
import { Icon, iconsList } from "../../Icon";
import styles from "./HorizontalCard.module.scss";

const HorizontalCard = ({
	className,
	style,
	image,
	logo,
	logoPosition,
	uppercase,
	header,
	headingSize,
	color,
	content,
	link,
	button,
	backgroundColor,
	renderImage,
	renderLink,
}) => (
	<div className={styles.horizontalCard}>
		<div className={styles.horizontalCardContainer} style={style}>
			<div
				className={cx(
					styles.horizontalCardContentContainer,
					className,
					!link?.text && !content ? styles.centered : null
				)}
				style={{ backgroundColor: backgroundColor }}>
				<div className={styles.horizontalCardContentTextContainer}>
					{header && (
						<Card.Header
							className={styles.horizontalCardHeading}
							style={{ color: color }}
							uppercase={uppercase}
							size={headingSize}>
							{header}
						</Card.Header>
					)}
					{content && (
						<div
							className={styles.horizontalCardText}
							style={{ color: color }}
							dangerouslySetInnerHTML={{ __html: content }}
						/>
					)}
				</div>

				{link && (
					<Card.Link
						to={link.to}
						target={link.target}
						className={styles.horizontalCardLink}
						icon={link.icon}
						renderLink={renderLink}>
						{link.text}
						{link.text && <Icon name="arrow-right" />}
					</Card.Link>
				)}
				{button && (
					<Card.Button
						to={button.to}
						target={button.target}
						className={styles.horizontalCardButtonLink}
						icon={button.icon}
						iconPlacement={button.iconPlacement}
						onClick={button.onClick}
						linkComponentProps={{ renderLink }}>
						{button.text}
					</Card.Button>
				)}
			</div>
			<div className={styles.horizontalCardImageContainer}>
				{image && (
					<Card.Image
						className={styles.horizontalCardImage}
						src={image.src}
						alt={image.alt}
						imageComponentProps={{ renderImage }}
					/>
				)}
				{logo && <Card.Logo logo={logo} logoPosition={logoPosition} />}
			</div>
		</div>
		{link?.to && (
			<Card.AsLink
				link={link.to}
				target={link.target}
				text={header || link.text ? header || link.text : ""}
				className={styles.horizontalCardAsLink}
				renderLink={renderLink}
			/>
		)}
	</div>
);

HorizontalCard.propTypes = {
	className: PropTypes.string,
	style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	image: PropTypes.shape({
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		alt: PropTypes.string,
		src: PropTypes.string,
	}).isRequired,
	logo: PropTypes.shape({
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		alt: PropTypes.string,
		src: PropTypes.string,
	}),
	logoPosition: PropTypes.oneOf(["top-left", "top-right", "bottom-left", "bottom-right"]),
	header: PropTypes.string,
	color: PropTypes.string,
	headingSize: PropTypes.string,
	uppercase: PropTypes.bool,
	content: PropTypes.string,
	/**
	 * Buttons act as links but look like buttons in the UI . They will be applied to card and cannot be used with links
	 */
	button: PropTypes.shape({
		icon: PropTypes.oneOf(iconsList),
		iconPlacement: PropTypes.oneOf(["right", "left"]),
		onClick: PropTypes.func,
		label: PropTypes.string,
		to: PropTypes.string,
		target: PropTypes.string,
		text: PropTypes.string,
	}),
	/**
	 * Links will be applied to card, cannot be used with buttons
	 */
	link: PropTypes.shape({
		to: PropTypes.string.isRequired,
		target: PropTypes.string,
		icon: PropTypes.oneOf(iconsList),
		text: PropTypes.string,
	}),
	/**
	 * Changes background colour of the the hero card
	 */
	backgroundColor: PropTypes.string,
	renderImage: PropTypes.func,
	renderLink: PropTypes.func,
};

HorizontalCard.defaultProps = {
	className: undefined,
	style: {},
	logo: undefined,
	logoPosition: "bottom-right",
	header: undefined,
	color: "#FFFFFF",
	headingSize: "h4",
	uppercase: true,
	content: undefined,
	button: undefined,
	link: undefined,
	backgroundColor: "#595959",
	renderImage: undefined,
	renderLink: undefined,
};

export default HorizontalCard;
export { HorizontalCard };
